import { Button } from '@wot/components';
import React, { useState, useRef } from 'react';
import { API_URL } from '../utils/constants';

interface ImageUploadProps {
  label: string;
  onUploadComplete: (url: string) => void;
  previewUrl?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ label, onUploadComplete, previewUrl }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(previewUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsLoading(true);
      setError(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          onUploadComplete(result.data);
        } else {
          setError('Upload failed');
        }
      } catch (err) {
        setError('An error occurred during upload');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1 flex items-center">
        {imagePreview ? (
          <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded" />
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded flex items-center justify-center text-gray-400">No Image</div>
        )}
        <div className="ml-4">
          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
          <Button text={isLoading ? 'Uploading...' : 'Change'} onClick={handleButtonClick} disabled={isLoading} />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
      </div>
    </div>
  );
};
